<ng-template
  #menuLink
  let-id="id"
  let-route="route"
  let-icon="icon"
  let-label="label"
  let-states="states"
  let-folders="folders"
  let-params="params"
  let-hasPopup="hasPopup"
>
  <a
    [id]="id"
    class="menu-item"
    [uiSref]="route"
    [uiParams]="folders ? { folderId: null } : params || {}"
    [ngClass]="{ selected: isNavSelected(states, params) }"
    [attr.aria-current]="isNavSelected(states, params) ? 'page' : null"
    [attr.aria-haspopup]="hasPopup || null"
    matTooltip="{{ label }}"
    matTooltipPosition="right"
    matTooltipClass="menu-tooltip"
    [matTooltipDisabled]="state !== 'collapse'"
  >
    <mat-icon aria-hidden="true" [svgIcon]="icon"></mat-icon>
    <span>{{ label }}</span>
  </a>
</ng-template>

<ng-template
  #menuButton
  let-id="id"
  let-icon="icon"
  let-label="label"
  let-tooltip="tooltip"
  let-addon="addon"
  let-class="class"
  let-addonClass="addonClass"
  let-states="states"
  let-clickHandler="clickHandler"
  let-hasPopup="hasPopup"
  let-expanded="expanded"
>
  <button
    [id]="id"
    class="menu-item"
    (click)="clickHandler && clickHandler()"
    [class]="class"
    [ngClass]="{ selected: states && states.indexOf(currentRoute) > -1 }"
    [attr.aria-haspopup]="hasPopup || null"
    [attr.aria-expanded]="expanded != null ? '' + expanded : null"
    matTooltip="{{ tooltip }}"
    matTooltipPosition="right"
    matTooltipClass="menu-tooltip"
    [matTooltipDisabled]="state !== 'collapse'"
  >
    @if (icon) {
      <mat-icon aria-hidden="true" [svgIcon]="icon"></mat-icon>
    } @else {
      <initials-badge
        [name]="label"
        [size]="22"
        class="tw-m-[-1px]"
      ></initials-badge>
    }
    <div class="menu-item-button">
      <div
        class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-w-full tw-max-w-full tw-min-w-[0]"
      >
        <span class="tw-truncate">{{ label }}</span>
        @if (addon) {
          <mat-icon
            aria-hidden="true"
            [svgIcon]="addon"
            class="addon {{ addonClass }}"
          ></mat-icon>
        }
      </div>
    </div>
  </button>
</ng-template>

<div id="sidebar" class="tw-h-full" [ngClass]="state">
  @if (showMenu) {
    <div class="sidebar-title">
      <div
        id="logo"
        (mouseenter)="
          titleLogo = isMobile() || state === 'expand' ? 'rise-logo' : 'sidebar'
        "
        (mouseleave)="titleLogo = 'rise-logo'"
        (click)="onLogoClick()"
      >
        <button mat-icon-button aria-label="Rise logo" tabindex="0">
          <mat-icon [svgIcon]="titleLogo"></mat-icon>
        </button>
        <h1>Rise Vision</h1>
      </div>

      <button
        mat-icon-button
        id="sidenav-toggle"
        (click)="toggleSidenav.emit()"
        aria-label="Toggle sidebar"
        *ngIf="!isMobile()"
        tabindex="0"
      >
        <mat-icon svgIcon="sidebar"></mat-icon>
      </button>
    </div>

    <div class="divider tw-mb-4"></div>

    @if (companyStateService.getSelectedCompanyName()) {
      @if (!showCompanyTree()) {
        <div
          [ngClass]="{
            'sub-company-alert': companyStateService.isSubcompanySelected(),
          }"
          [title]="companyStateService.getSelectedCompanyName()"
          [matMenuTriggerFor]="
            !userStateService.isAssignedPublisher() ? companyMenu : null
          "
          (menuOpened)="handleMenuOpened()"
          (menuClosed)="handleMenuClosed()"
        >
          <ng-container
            *ngTemplateOutlet="
              menuButton;
              context: {
                id: 'company-menu-button',
                label: companyStateService.getSelectedCompanyName(),
                tooltip: 'Company',
                class: userStateService.isAssignedPublisher()
                  ? 'ap-role'
                  : null,
                addon: userStateService.isAssignedPublisher()
                  ? null
                  : 'chevron-down',
              }
            "
          ></ng-container>
        </div>
        <mat-menu
          #companyMenu="matMenu"
          class="company-menu"
          panelClass="company-menu"
          [overlapTrigger]="true"
        >
          <ng-template matMenuContent>
            <select-company (closeMenu)="handleCloseMenu()"></select-company>
          </ng-template>
        </mat-menu>
      } @else {
        <div
          [ngClass]="{
            'sub-company-alert': companyStateService.isSubcompanySelected(),
          }"
          [title]="companyStateService.getSelectedCompanyName()"
          cdkOverlayOrigin
          #companyMenuOrigin="cdkOverlayOrigin"
          (click)="openCompanyPanel()"
        >
          <ng-container
            *ngTemplateOutlet="
              menuButton;
              context: {
                id: 'company-menu-button',
                label: companyStateService.getSelectedCompanyName(),
                tooltip: 'Company',
                class: userStateService.isAssignedPublisher()
                  ? 'ap-role'
                  : null,
                addon: userStateService.isAssignedPublisher()
                  ? null
                  : 'chevron-down',
                hasPopup: userStateService.isAssignedPublisher()
                  ? null
                  : 'dialog',
                expanded: userStateService.isAssignedPublisher()
                  ? null
                  : companyPanelOpen(),
              }
            "
          ></ng-container>
        </div>
        <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="companyMenuOrigin"
          [cdkConnectedOverlayOpen]="companyPanelOpen()"
          [cdkConnectedOverlayPositions]="companyPanelPositions"
          [cdkConnectedOverlayPush]="true"
          [cdkConnectedOverlayHasBackdrop]="true"
          [cdkConnectedOverlayFlexibleDimensions]="true"
          [cdkConnectedOverlayViewportMargin]="companyPanelViewportMargin()"
          [cdkConnectedOverlayPanelClass]="'company-menu-overlay'"
          (overlayOutsideClick)="closeCompanyPanel()"
          (backdropClick)="closeCompanyPanel()"
          (overlayKeydown)="onCompanyPanelKeydown($event)"
          (detach)="onCompanyPanelDetach()"
        >
          <div
            class="company-menu-overlay-dialog"
            role="dialog"
            aria-label="Select company"
            cdkTrapFocus
            cdkTrapFocusAutoCapture
          >
            <select-company-tree
              (closeMenu)="closeCompanyPanel()"
            ></select-company-tree>
          </div>
        </ng-template>
      }
    }
    @if (!rvshareAppDetectionService.isScreenShareApp()) {
      <div id="sidebar-nav">
        <ng-container
          *ngTemplateOutlet="menuLink; context: navOptions['presentations']"
        ></ng-container>

        @if (showSchedulesNav) {
          <ng-container
            *ngTemplateOutlet="menuLink; context: navOptions['schedules']"
          ></ng-container>
        }

        @if (showDisplaysNav) {
          <ng-container
            *ngTemplateOutlet="menuLink; context: navOptions['displays']"
          ></ng-container>
        }

        <div class="divider tw-my-2"></div>

        @if (showScreenSharingNav) {
          @if (featuresService.isFeatureAvailable('screen_sharing')) {
            <ng-container
              *ngTemplateOutlet="
                menuLink;
                context: navOptions['screen-sharing']
              "
            ></ng-container>
          } @else {
            <ng-container
              *ngTemplateOutlet="
                menuButton;
                context: navOptions['screen-sharing-upgrade']
              "
            ></ng-container>
          }
        }
        @if (!userStateService.isAssignedPublisher()) {
          <ng-container
            *ngTemplateOutlet="menuLink; context: navOptions['storage']"
          ></ng-container>
        }
        <div class="divider tw-my-2"></div>

        @if (canAccessCapAlerts()) {
          @if (featuresService.isFeatureAvailable('alerts')) {
            <ng-container
              *ngTemplateOutlet="menuLink; context: navOptions['cap-alerts']"
            ></ng-container>
          } @else {
            <ng-container
              *ngTemplateOutlet="
                menuButton;
                context: navOptions['cap-alerts-upgrade']
              "
            ></ng-container>
          }
        }
        <ng-container
          *ngTemplateOutlet="menuLink; context: navOptions['integrations']"
        ></ng-container>

        @if (canAccessSettings()) {
          <ng-container
            *ngTemplateOutlet="menuLink; context: navOptions['settings']"
          ></ng-container>
        }
      </div>
    }
  }

  <div class="plan-container">
    <plan-banner [collapsed]="state === 'collapse'" *requireRole="'!ap'" />
  </div>
</div>
